/**
 * Build ElevenLabs startSession options, optionally pinning a preview agent branch.
 *
 * The React/client SDK does not yet expose branchId, but the public WebSocket
 * endpoint accepts ?branch_id= (same as the ElevenLabs dashboard tester).
 *
 * Preview branches are localhost-only so a mis-set env never ships to prod users.
 *
 * `userId` is ElevenLabs' filterable end-user identifier (we pass email).
 * Org/user ids live in dynamicVariables for conversation detail + prompts.
 */

import { getUserEmail } from "./orgHelpers";

const PREVIEW_BRANCH_ID = process.env.REACT_APP_ELEVENLABS_BRANCH_ID || "";

function isLocalhostHost(hostname) {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  );
}

export function getAssistantPreviewBranchId() {
  if (typeof window === "undefined") return null;
  if (!PREVIEW_BRANCH_ID) return null;
  if (!isLocalhostHost(window.location.hostname)) return null;
  return PREVIEW_BRANCH_ID;
}

/**
 * @param {object} args
 * @param {string} args.agentId
 * @param {Record<string, string|number|boolean>} [args.dynamicVariables]
 * @param {object} [args.overrides] — SessionConfig overrides (e.g. agent.firstMessage)
 * @param {string} [args.userId] — defaults to signed-in user email
 */
export function buildAssistantSessionOptions({
  agentId,
  dynamicVariables,
  overrides,
  userId,
}) {
  const branchId = getAssistantPreviewBranchId();
  const resolvedUserId =
    (typeof userId === "string" && userId.trim()) || getUserEmail() || undefined;
  const base = {
    connectionType: "websocket",
    textOnly: true,
    dynamicVariables,
    ...(overrides ? { overrides } : {}),
    ...(resolvedUserId ? { userId: resolvedUserId } : {}),
  };

  if (!branchId) {
    return { ...base, agentId };
  }

  // Public agents: pass a full WSS URL via signedUrl so we can append branch_id.
  // (SessionConfig's agentId path cannot take extra query params today.)
  const origin = "wss://api.elevenlabs.io";
  const signedUrl =
    `${origin}/v1/convai/conversation` +
    `?agent_id=${encodeURIComponent(agentId)}` +
    `&branch_id=${encodeURIComponent(branchId)}`;

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.info(
      `[assistant] Using ElevenLabs preview branch ${branchId} (localhost only)`
    );
  }

  return { ...base, signedUrl };
}
